// Instrumentação leve do funil de login/cadastro do gestor.
//
// Antes desta camada só a conversão final era medida (ads.ts → trackSignupConversion,
// disparada quando a oficina é criada). Resultado: dava pra saber quantos completavam,
// mas não ONDE o lead abandonava. Estes eventos cobrem cada passo, permitindo achar
// o ponto de fuga (tela fria? SMS/OTP? formulário de cadastro?).
//
// Fan-out para gtag (GA4/Google Ads), Microsoft Clarity e Sentry (métricas).
// Todos são no-op se a lib não estiver carregada (dev/CI sem as envs), e qualquer
// erro é silenciado — analytics nunca pode quebrar o fluxo de login.

import { Sentry } from '@/lib/sentry';

export type LoginFunnelStep =
  | 'login_entry_view' // tela inicial (social + telefone) exibida
  | 'login_method_selected' // usuário escolheu um método (google/apple/phone)
  | 'otp_sent' // SMS de OTP enviado
  | 'otp_confirmed' // código confirmado (autenticação ok)
  | 'registration_shown' // formulário de cadastro exibido (needsRegistration)
  | 'registration_submitted' // cadastro submetido (create/join)
  | 'registration_completed'; // conta + oficina prontas

interface AnalyticsWindow {
  gtag?: (...args: unknown[]) => void;
  clarity?: (...args: unknown[]) => void;
}

export function trackLoginFunnel(step: LoginFunnelStep, params?: Record<string, string>): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as AnalyticsWindow;

  try {
    w.gtag?.('event', step, { ...params });
  } catch {
    // gtag ausente ou bloqueado por adblock: ignora.
  }

  try {
    // Clarity: smart event (aparece no funil do painel) + tag para filtrar sessões.
    w.clarity?.('event', step);
    if (params?.method) w.clarity?.('set', 'login_method', params.method);
  } catch {
    // Clarity ausente: ignora.
  }

  try {
    // Sentry: 1 contador `mustauto.funnel.login` com tag `step` — o dashboard
    // faz count() group by step pra montar o funil. `login_method` permite
    // segmentar por provedor.
    Sentry.metrics.count('mustauto.funnel.login', 1, {
      attributes: { step, ...(params?.method ? { login_method: params.method } : {}) },
    });
  } catch {
    // Sentry/metrics ausente: ignora.
  }
}

/**
 * Marca a sessão como demonstração nas ferramentas de analytics, para os times
 * SEGMENTAREM/FILTRAREM o demo fora das análises de negócio (não pode poluir).
 * Idempotente — pode ser chamada a cada carga da sessão demo.
 */
export function tagDemoSession(): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as AnalyticsWindow;
  try {
    w.clarity?.('set', 'is_demo', 'true');
  } catch {
    // Clarity ausente: ignora.
  }
  try {
    w.gtag?.('set', 'user_properties', { is_demo: true });
  } catch {
    // gtag ausente: ignora.
  }
}

/** Sinal de INTERESSE: o visitante abriu a demonstração ("abriu a demo quero saber"). */
export function trackDemoOpened(): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as AnalyticsWindow;
  try {
    w.gtag?.('event', 'demo_opened');
  } catch {
    // gtag ausente: ignora.
  }
  try {
    w.clarity?.('event', 'demo_opened');
  } catch {
    // Clarity ausente: ignora.
  }
}
